import { useCallback, useContext, useEffect, useState } from 'react';

import { AppContext } from '@r-app/app-context';

import { PageFontFactorContext, PageReportComputedFactorContext } from './page-font-factor-context';

export type BlueprintPageFontControlProps = {
	pageNumber: number;
	/** Stable per-sheet override prefix (page.overrideScope). Persists the font factor so it survives a view switch — page numbers shift, so this must NOT be the page number. */
	scope: string;
	children: React.ReactNode;
};

const FONT_FACTOR_MIN = 0.5;
const FONT_FACTOR_MAX = 3.0;
const FONT_FACTOR_STEP = 0.1;

// off the page's top-right corner: never overlaps page content, reachable via the horizontal scroll
const CONTROL_STYLE: React.CSSProperties = {
	position: 'absolute',
	left: '100%',
	marginLeft: '12px',
	top: '0px',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: '2px',
	padding: '4px',
	backgroundColor: 'rgba(255,255,255,0.95)',
	border: '1px solid #ccc',
	borderRadius: '4px',
	fontFamily: "'Inter', sans-serif",
	color: '#333',
	boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
	userSelect: 'none',
	zIndex: 5
};

const BTN_STYLE: React.CSSProperties = {
	width: '24px',
	height: '20px',
	border: '1px solid #999',
	background: '#fff',
	color: '#333',
	fontSize: '12px',
	lineHeight: '1',
	cursor: 'pointer',
	borderRadius: '3px',
	padding: 0
};

const VALUE_STYLE: React.CSSProperties = {
	fontSize: '10px',
	color: '#555',
	minWidth: '24px',
	textAlign: 'center'
};

const clamp = (n: number): number => {
	return Math.min(FONT_FACTOR_MAX, Math.max(FONT_FACTOR_MIN, Math.round(n * 100) / 100));
};

/**
 * Wraps a single blueprint page and renders an independent font-size control
 * to the right of the page card. The control updates only this page's font
 * factor via `PageFontFactorContext`. Defaults to the controller's
 * `dimensionFontSizeFactor`; the user can step up/down and double-click the
 * value to reset.
 */
export const BlueprintPageFontControl = (props: BlueprintPageFontControlProps): React.ReactElement => {
	const { pageNumber, scope, children } = props;
	const mainContext = useContext(AppContext);
	const kitchen = mainContext?.controller?.kitchen ?? null;
	const controllerFactor = mainContext?.controller?.options?.dimensionFontSizeFactor ?? 1.2;

	// Persisted per-page override (blueprintOverrides, keyed by the stable scope) so the font size
	// survives switching to the working view and back — local state was lost on unmount (GitHub #293).
	// Local state drives the UI; the kitchen model is the write-through persistence backing store.
	// setBlueprintOverride publishes ('kitchen','updated'), which the BP-view container deliberately
	// does NOT subscribe to (would drop the EditableText caret mid-edit — see container's
	// useSubscription). Without local state the stepper clicks mutate the model but never re-render,
	// so the font size never changes on-screen (GitHub #374).
	const fontKey = `${scope}.fontFactor`;
	const readStored = (): number | null => {
		const s = kitchen?.blueprintOverrides?.[fontKey];
		return s === undefined ? null : parseFloat(s);
	};
	const [override, setOverride] = useState<number | null>(readStored);

	// Re-sync when the kitchen instance changes (design load, controller swap): the fresh
	// blueprint may already have a stored fontFactor under this scope.
	useEffect(() => {
		const s = kitchen?.blueprintOverrides?.[fontKey];
		setOverride(s === undefined ? null : parseFloat(s));
	}, [kitchen, fontKey]);

	// The viewport/panel-card reports its computed factor (with zoom compensation)
	// so we display the actual value being used — not the raw controller default.
	const [computedDefault, setComputedDefault] = useState<number>(controllerFactor);
	const currentValue = override ?? computedDefault;

	const stepUp = useCallback(() => {
		const next = clamp(currentValue + FONT_FACTOR_STEP);
		setOverride(next);
		kitchen?.setBlueprintOverride(fontKey, String(next));
	}, [kitchen, fontKey, currentValue]);

	const stepDown = useCallback(() => {
		const next = clamp(currentValue - FONT_FACTOR_STEP);
		setOverride(next);
		kitchen?.setBlueprintOverride(fontKey, String(next));
	}, [kitchen, fontKey, currentValue]);

	const reset = useCallback(() => {
		setOverride(null);
		kitchen?.clearBlueprintOverride(fontKey);
	}, [kitchen, fontKey]);

	return (
		<PageFontFactorContext.Provider value={override}>
		<PageReportComputedFactorContext.Provider value={setComputedDefault}>
			<div style={{ position: 'relative', width: '1100px', flexShrink: 0, margin: '0 auto' }}>
				{children}
				<div style={CONTROL_STYLE} data-html2canvas-ignore="true" title={`Font size for page ${pageNumber} — double-click value to reset`}>
					<button
						type="button"
						onClick={stepUp}
						style={BTN_STYLE}
						aria-label="Increase font size"
					>
						▲
					</button>
					<div
						style={VALUE_STYLE}
						onDoubleClick={reset}
						title="Double-click to reset to default"
					>
						{currentValue.toFixed(1)}
					</div>
					<button
						type="button"
						onClick={stepDown}
						style={BTN_STYLE}
						aria-label="Decrease font size"
					>
						▼
					</button>
				</div>
			</div>
		</PageReportComputedFactorContext.Provider>
		</PageFontFactorContext.Provider>
	);
};
